import { useState, useEffect, memo } from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import ContactPageOutlinedIcon from '@mui/icons-material/ContactPageOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import LabelClickUpMenu from '@AgileRepresentationalDisplay/LabelClickUpMenu';
import Comment from '@IssueConsoleRepresentationalDisplay/Comment';
import ErrorHandler from '@Root/ErrorHandler';
import '@Partials/_ComStyles.scss';
import './History.scss';

function History(props) {
    const [historyUser, setHistoryUser] = useState({});
    const [oldAssigneeUser, setOldAssigneeUser] = useState({ fullName: 'Unassigned', userName: 'unassigned', id: 0 });
    const [newAssigneeUser, setNewAssigneeUser] = useState({ fullName: 'Unassigned', userName: 'unassigned', id: 0 });
    // Starts null (not {}) so a 'history'-type interaction can tell "not resolved yet" apart
    // from "resolved, and this history entry genuinely has no recognized property" — see the
    // guard below, right before the generic event-card fallback.
    const [history, setHistory] = useState(null);
    const [priorityList, setPriorityList] = useState([]);
    const [labelLinkData, setLabelLinkData] = useState(null);

    const colorList = [
        '#2784B9', '#40c47d', '#FDB16C', '#b06060', '#f77474', '#6080b0', '#9a60b0', '#6cb060',
        '#b0ae60', '#65412b', '#cc9963', '#e1bd83', '#60748b', '#744700', '#c8d579', '#5f8fba',
        '#3e6073', '#8e6dc1', '#473785', '#9158a9', '#7f3f7a', '#583e84', '#073763', '#20124d',
        '#3d124d', '#64113f', '#780448', '#003e23', '#108764', '#1a4c3d', '#c8d77f', '#11888b',
        '#1a95bd', '#0d6c7b', '#25b56e', '#006e3d'
    ];

    const errorHandler = new ErrorHandler();
    const userInfoService = new UserInfoService();
    const className = 'history';

    const parseUserSafe = (response) => {
        if (!response?.body) return null;
        try { return JSON.parse(response.body); } catch { return null; }
    };

    useEffect(() => {
        renderData();
    }, [props]);

    const renderData = async () => {
        setPriorityList(props?.priorityList);
        let historyObj;

        if (props.userInteraction.linkType === 'history') {
            historyObj = props.historyList.find((history) => history.id === props.userInteraction.linkId);
            setHistory(historyObj);

            try {
                if (historyObj?.property === 'statusUpdate') {
                    if (historyObj?.details?.updatedByType === 'System') {
                        setHistoryUser({ fullName: 'System', userName: 'System', id: 0 });
                    } else {
                        const userObj = parseUserSafe(await userInfoService.getUserById(historyObj?.details?.updatedById));
                        if (userObj) setHistoryUser(userObj);
                    }
                }
                if (historyObj?.property === 'assigneeUpdate') {
                    let oldAssigneeUserId = historyObj?.details?.oldAssignee;
                    if (oldAssigneeUserId !== 0) {
                        const userObj = parseUserSafe(await userInfoService.getUserById(oldAssigneeUserId));
                        if (userObj) setOldAssigneeUser(userObj);
                    }
                    let newAssigneeUserId = historyObj?.details?.newAssignee;
                    if (newAssigneeUserId !== 0) {
                        const userObj = parseUserSafe(await userInfoService.getUserById(newAssigneeUserId));
                        if (userObj) setNewAssigneeUser(userObj);
                    }
                    const updaterObj = parseUserSafe(await userInfoService.getUserById(historyObj?.details?.updatedById));
                    if (updaterObj) setHistoryUser(updaterObj);
                }
                if (historyObj?.property === 'Label') {
                    const userObj = parseUserSafe(await userInfoService.getUserById(historyObj?.changeById));
                    if (userObj) setHistoryUser(userObj);

                    if (historyObj?.registryLinkObj) {
                        const registryType = historyObj.registryLinkObj.registryType;
                        const registryId = historyObj.registryLinkObj.registryId;
                        let labelName = 'Unknown Label';
                        try {
                            let response;
                            if (registryType === 'labelCard') {
                                const LabelCardService = (await import('@Helpers/URLHelpers/WeboutService/LabelCardService')).default;
                                response = await new LabelCardService().getById(registryId);
                            } else if (registryType === 'nameCard') {
                                const NameCardService = (await import('@Helpers/URLHelpers/WeboutService/NameCardService')).default;
                                response = await new NameCardService().getById(registryId);
                            } else if (registryType === 'orgCard') {
                                const OrganizationCardService = (await import('@Helpers/URLHelpers/WeboutService/OrganizationCardService')).default;
                                response = await new OrganizationCardService().getById(registryId);
                            }
                            if (response) {
                                const parsed = JSON.parse(response.body);
                                labelName = parsed.name || 'Unknown Label';
                                setLabelLinkData({ ...historyObj.registryLinkObj, name: labelName, photo: parsed.photo });
                            }
                        } catch (err) {
                            errorHandler.logError('Label fetch in History', err);
                            setLabelLinkData({ ...historyObj.registryLinkObj, name: labelName });
                        }
                    }
                }
                if (historyObj?.property === 'reporterUpdated') {
                    const userObj = parseUserSafe(await userInfoService.getUserById(historyObj?.changeById));
                    if (userObj) setHistoryUser(userObj);
                }
                if (historyObj?.property === 'priorityUpdate') {
                    const userObj = parseUserSafe(await userInfoService.getUserById(historyObj?.details?.updatedById));
                    if (userObj) setHistoryUser(userObj);
                }
            } catch (err) {
                errorHandler.logError('TicketView History', err);
            }
        }
    };

    const getInitials = (string) => {
        if (!!string && string.length > 0) {
            var names = string?.split(' '),
                initials = names[0]?.substring(0, 1)?.toUpperCase();
            if (names?.length > 1) initials += names[names?.length - 1]?.substring(0, 1)?.toUpperCase();
            return initials;
        }
        return 'N/A';
    };

    const dataURLtoFile = (dataurl) => {
        var arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        return new File([u8arr], { type: mime });
    };

    const splitUserNames = (user) => {
        if (user && user !== null) {
            const parts = user.fullName?.trim().split(' ').filter(Boolean);
            if (!parts || parts.length === 0) return '';
            if (parts.length === 1) return parts[0];
            return parts[0] + ' ' + parts[parts.length - 1][0] + '.';
        }
        return '';
    };

    const formatTime = (timestamp) => {
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(timestamp);
    };

    const renderAvatar = (user, size = 28) => {
        if (!user) return <Avatar sx={{ width: size, height: size, fontSize: size * 0.38, fontWeight: 600 }}>?</Avatar>;
        return user.photo?.startsWith?.('data:') ? (
            <Avatar src={URL.createObjectURL(dataURLtoFile(user.photo))} sx={{ width: size, height: size }} />
        ) : (
            <Avatar
                sx={{
                    bgcolor: colorList[user?.fullName?.charCodeAt(0) % colorList?.length],
                    width: size,
                    height: size,
                    fontSize: size * 0.38,
                    fontWeight: 600
                }}
            >
                {getInitials(user?.fullName)}
            </Avatar>
        );
    };

    const renderStatusChip = (statusId, label) => {
        const statusColor = !!props.adminFieldList?.find((field) => field.title === 'status')
            ? JSON.parse(props.adminFieldList?.find((field) => field.title === 'status')?.options)?.find(
                  (option) => option.id === statusId
              )?.color
            : 'var(--color-primaryBlue)';

        return (
            <Chip
                label={label || 'N/A'}
                size="small"
                sx={{
                    bgcolor: alpha(statusColor || '#2784B9', 0.12),
                    color: statusColor,
                    border: `1px solid ${alpha(statusColor || '#2784B9', 0.35)}`,
                    fontWeight: 600,
                    fontSize: '0.6875rem',
                    height: 22,
                    textTransform: 'capitalize',
                }}
                variant="filled"
            />
        );
    };

    const renderPriorityChip = (priorityValue) => {
        const priorityColor = priorityList?.find((priority) => priority.value === priorityValue)?.color || '#9aa0a6';
        return (
            <Chip
                label={priorityValue || 'N/A'}
                size="small"
                sx={{
                    bgcolor: alpha(priorityColor, 0.12),
                    color: priorityColor,
                    border: `1px solid ${alpha(priorityColor, 0.35)}`,
                    fontWeight: 600,
                    fontSize: '0.6875rem',
                    height: 22,
                    textTransform: 'capitalize',
                }}
                variant="filled"
            />
        );
    };

    // A 'history'-type interaction goes through one render pass before its effect resolves
    // `history` — without this guard, that pass falls all the way through every specific
    // property check below into the generic event-card fallback with nothing to show (blank
    // avatar, blank action label): a stray white card that never explains itself. Wait for the
    // real object instead — this also covers a linkId that never resolves to any entry at all.
    if (props.userInteraction.linkType === 'history' && !history) return null;

    if (history?.property?.toLowerCase() === 'clientcomment') return null;

    const prop = history?.property?.toLowerCase();

    // ── Create event ───────────────────────────────────────────────────────
    if (prop === 'create') {
        return (
            <Box className={className + '__createWrap'}>
                <Box className={className + '__createLine'} />
                <Box className={className + '__createChip'}>
                    <AddCircleOutlineIcon className={className + '__createIcon'} />
                    <Typography className={className + '__createLabel'}>Issue created</Typography>
                    <Typography className={className + '__createTime'}>{formatTime(history?.timeStamp)}</Typography>
                </Box>
                <Box className={className + '__createLine'} />
            </Box>
        );
    }

    // ── Comment ────────────────────────────────────────────────────────────
    // No spine dot here — the WhatsApp-style bubble already carries its own avatar and doesn't
    // need a timeline marker explaining what kind of event it is (unlike a status/priority/
    // assignee/label change, a comment isn't really "an event" in that sense). The spine itself
    // stays as a plain spacer so the message still lines up under the same 32px indent as every
    // other row in the thread.
    if (props.userInteraction.linkType === 'comment') {
        return (
            <Box className={className}>
                <Box className={className + '__spine'} />
                <Box className={className + '__content ' + className + '__commentWrap'}>
                    <Comment
                        ticket={props?.ticket}
                        userInteraction={props.userInteraction}
                        commentList={props.commentList}
                    />
                </Box>
            </Box>
        );
    }

    // ── Determine icon & dot variant for history events ───────────────────
    let dotVariant = 'status';
    let EventIcon = UpdateOutlinedIcon;
    let actionLabel = '';

    if (prop === 'statusupdate') {
        dotVariant = 'status';
        EventIcon = UpdateOutlinedIcon;
        actionLabel = 'Status changed';
    } else if (prop === 'priorityupdate') {
        dotVariant = 'priority';
        EventIcon = FlagOutlinedIcon;
        actionLabel = 'Priority changed';
    } else if (prop === 'assigneeupdate') {
        dotVariant = 'assignee';
        EventIcon = PersonOutlinedIcon;
        actionLabel = 'Assignee changed';
    } else if (history?.property === 'Label') {
        dotVariant = 'label';
        EventIcon = SellOutlinedIcon;
        actionLabel = history?.action === 'add' ? 'Label added' : 'Label removed';
    } else if (prop === 'reporterupdated') {
        dotVariant = 'reporter';
        EventIcon = ContactPageOutlinedIcon;
        actionLabel =
            history?.action === 'reporterDetailsUpdated' ? 'Reporter details changed'
            : history?.action === 'createdByFullName' ? 'Reporter name changed'
            : history?.action === 'createdByEmail' ? 'Reporter email changed'
            : history?.action === 'createdByContactNumber' ? 'Reporter phone changed'
            : 'Reporter updated';
    }

    // ── Generic event card ─────────────────────────────────────────────────
    return (
        <Box className={className}>
            {/* Left spine */}
            <Box className={className + '__spine'}>
                <Box className={`${className}__dot ${className}__dot--${dotVariant}`}>
                    <EventIcon />
                </Box>
                <Box className={className + '__connector'} />
            </Box>

            {/* Right content */}
            <Box className={className + '__content'}>
                <Box className={className + '__card'}>
                    {/* Header */}
                    <Box className={className + '__cardHeader'}>
                        {renderAvatar(historyUser)}
                        <Box className={className + '__cardHeaderText'}>
                            <Typography className={className + '__userName'}>{splitUserNames(historyUser)}</Typography>
                            <Typography className={className + '__time'}>{formatTime(history?.timeStamp)}</Typography>
                        </Box>
                    </Box>

                    {/* Body */}
                    <Box className={className + '__cardBody'}>
                        <Typography className={className + '__action'}>{actionLabel}</Typography>

                        {/* Status */}
                        {prop === 'statusupdate' && (
                            <>
                                <Box className={className + '__changeRow'}>
                                    {renderStatusChip(
                                        history?.details?.oldStatus,
                                        JSON.parse(props.adminFieldList?.find((f) => f.title === 'status')?.options || '[]')?.find(
                                            (o) => o.id === history?.details?.oldStatus
                                        )?.title
                                    )}
                                    <ArrowForwardIcon className={className + '__arrow'} />
                                    {renderStatusChip(
                                        history?.details?.newStatus,
                                        JSON.parse(props.adminFieldList?.find((f) => f.title === 'status')?.options || '[]')?.find(
                                            (o) => o.id === history?.details?.newStatus
                                        )?.title
                                    )}
                                </Box>
                                {history?.details?.silent && (
                                    <Box className={className + '__silentBadge'}>
                                        <NotificationsOffOutlinedIcon className={className + '__silentBadge__icon'} />
                                        <Typography className={className + '__silentBadge__text'}>
                                            Closed silently — reporter and agents weren't notified
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        )}

                        {/* Priority */}
                        {prop === 'priorityupdate' && (
                            <Box className={className + '__changeRow'}>
                                {renderPriorityChip(history?.details?.oldPriority)}
                                <ArrowForwardIcon className={className + '__arrow'} />
                                {renderPriorityChip(history?.details?.newPriority)}
                            </Box>
                        )}

                        {/* Assignee */}
                        {prop === 'assigneeupdate' && (
                            <Box className={className + '__changeRow'}>
                                <Box className={className + '__userChip'}>
                                    {renderAvatar(oldAssigneeUser, 20)}
                                    <Typography className={className + '__userChipName'}>{splitUserNames(oldAssigneeUser)}</Typography>
                                </Box>
                                <ArrowForwardIcon className={className + '__arrow'} />
                                <Box className={className + '__userChip'}>
                                    {renderAvatar(newAssigneeUser, 20)}
                                    <Typography className={className + '__userChipName'}>{splitUserNames(newAssigneeUser)}</Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Label */}
                        {history?.property === 'Label' && (
                            <Box className={className + '__labelRow'}>
                                {labelLinkData ? (
                                    <LabelClickUpMenu history={labelLinkData} />
                                ) : (
                                    <Chip label="Loading..." size="small" sx={{ bgcolor: 'var(--color-darkBackground)', color: 'var(--color-primaryGrey)', fontSize: '0.6875rem' }} />
                                )}
                            </Box>
                        )}

                        {/* Reporter */}
                        {prop === 'reporterupdated' && (
                            <Box className={className + '__reporterDetails'}>
                                <Typography className={className + '__reporterText'}>
                                    {history?.action === 'reporterDetailsUpdated' &&
                                        `${history?.newValue?.createdByFullName}, ${history?.newValue?.createdByEmail}, ${history?.newValue?.createdByContactNumber}`}
                                    {history?.action === 'createdByFullName' && history?.newValue?.createdByFullName}
                                    {history?.action === 'createdByEmail' && history?.newValue?.createdByEmail}
                                    {history?.action === 'createdByContactNumber' && history?.newValue?.createdByContactNumber}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default memo(History, arePropsEqual);

function arePropsEqual(oldProps, newProps) {
    return (
        oldProps.userInteraction.linkId === newProps.userInteraction.linkId &&
        oldProps.userInteraction.id === newProps.userInteraction.id &&
        oldProps.userInteraction.linkType === newProps.userInteraction.linkType
    );
}
